

import React, { useState } from 'react';
import {
    Box, Typography, Grid, TextField, Button,
    FormControl, InputLabel, Select, MenuItem,
    Paper, Table, TableBody, TableCell, TableContainer, TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
const HearingFollow = () => {
    // Search state
    const [opIpNumber, setOpIpNumber] = useState('');
    const [demographicData, setDemographicData] = useState(null);
    const [hearingAidData, setHearingAidData] = useState(null);
    const [date, setDate] = useState(''); // Placeholder for date state
    // Questionnaire state
    const [questionnaireType, setQuestionnaireType] = useState('');
    const [conditioningType, setConditioningType] = useState('');
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [downloadFormat, setDownloadFormat] = useState('excel');
    const [abhapScore, setAbhapScore] = useState('');
    const [hhiaScores, setHhiaScores] = useState({
        total: '',
        emotional: '',
        social: ''
    });

    // Mock data
    const mockDemographicData = {
        name: "John Doe",
        age: 62,
        gender: "Male",
        date: "2023-10-01",
        contact: "555-1234",
        address: "Karnataka, India",
        hearingLossType: "Sensorineural",
        audiogramDate: "2023-05-15"
    };

    const mockHearingAidData = {
        rightHA: { p1: 'Program 1', p2: 'Program 2', p3: 'Program 3' },
        leftHA: { p1: 'Program A', p2: 'Program B', p3: 'Program C' },
        features: {
            right: {
                directionMicrophone: "Activated",
                feedback: "Inactivated",
                dnr: "Activated",
                vc: "Enabled",
                dome: "Power",
                size: "2L"
            },
            left: {
                directionMicrophone: "Inactivated",
                feedback: "Activated",
                dnr: "Inactivated",
                vc: "Disabled",
                dome: "Double",
                size: "3L"
            }
        },
        earMould: {
            right: {
                type: "Shell",
                material: "Soft"
            },
            left: {
                type: "Canal",
                material: "Hard"
            }
        }
    };

    const handleSearchSubmit = () => {
        setDemographicData(mockDemographicData);
        setHearingAidData(mockHearingAidData);
    };

    const handleHhiaChange = (field, value) => {
        setHhiaScores(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDownloadClick = () => {
        setOpenPasswordModal(true);
    };

    const handleDownloadConfirm = async () => {
        const correctKey = "Varun@6361";

        // Validate key entered in modal
        if (password !== correctKey) {
            alert("Incorrect key! Download canceled.");
            setPassword('');
            setOpenPasswordModal(false);
            return;
        }

        setOpenPasswordModal(false);
        setLoading(true);
        setSaveStatus({
            message: `Requesting Hearing Evaluation Data  ${downloadFormat}...`,
            severity: "info",
        });

        try {
            const resp = await fetch(
                `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        table: "hearing_evaluation_data",
                        formName: "Hearing_Evaluation_Data",
                        format: downloadFormat,
                        key: password   // 🔥 send KEY instead of password
                    }),
                }
            );

            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                setSaveStatus({
                    message: `Failed: ${body.message || resp.statusText}`,
                    severity: "error",
                });
                return;
            }

            const blob = await resp.blob();
            const disposition = resp.headers.get("content-disposition") || "";

            let filename = `Hearing_Evaluation_Data_${Date.now()}.${downloadFormat === "excel" ? "xlsx" : "pdf"
                }`;

            const match = /filename="?([^"]+)"?/.exec(disposition);
            if (match && match[1]) filename = match[1];

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            setSaveStatus({
                message: `Download started: ${filename}`,
                severity: "success",
            });

        } catch (err) {
            console.error("Download error:", err);
            setSaveStatus({
                message: `Error downloading file: ${err.message}`,
                severity: "error",
            });
        } finally {
            setLoading(false);
            setPassword('');
        }
    };


    const handlePasswordModalClose = () => {
        setOpenPasswordModal(false);
        setPassword('');
    };
    const handleSubmit = () => {
        alert("Follow-up data submitted successfully!");
        setOpIpNumber('');
        setDemographicData(null);
        setHearingAidData(null);
        setQuestionnaireType('');
        setConditioningType('');
        setAbhapScore('');
        setHhiaScores({ total: '', emotional: '', social: '' });
    };

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 1400, margin: 'auto', mt: 4, mb: 6 }}>
            <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
                    Hearing Aid Follow-up
                </Typography>

                {/* Search Section */}
                <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                        Search Patient
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="IP/OP/Internal No"
                                value={opIpNumber}
                                onChange={(e) => setOpIpNumber(e.target.value)}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearchSubmit}
                                disabled={!opIpNumber}
                                sx={{ height: '56px' }}
                            >
                                Search Patient
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>



                {/* Display Hearing Aid Data in Two-Column Layout */}
                {hearingAidData && (
                    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                        <Box sx={{ position: 'absolute', top: 350, right: 70 }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadClick}
                                disabled={loading}
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.3s ease'
                                    }
                                }}
                            >
                                Download
                            </Button>
                        </Box>
                        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" sx={{ mb: 3 }}>
                            Hearing Aid Configuration
                        </Typography>

                        <Box mb={4} border={1} borderColor="divider" p={2} borderRadius={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    name="dateOfAssessment"
                                    label="Date of Assessment"
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Box>
                        {/* Programs Section - Two Columns */}
                        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" sx={{ mt: 2, mb: 2 }}>
                            Programs
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 4 }}>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Right HA</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Left HA</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="P1"
                                                        value={hearingAidData.rightHA.p1}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="P2"
                                                        value={hearingAidData.rightHA.p2}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="P3"
                                                        value={hearingAidData.rightHA.p3}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                        <TableCell>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="P1"
                                                        value={hearingAidData.leftHA.p1}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="P2"
                                                        value={hearingAidData.leftHA.p2}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="P3"
                                                        value={hearingAidData.leftHA.p3}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Features Section - Two Columns */}
                        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" sx={{ mt: 2, mb: 2 }}>
                            Features
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 4 }}>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Right HA Features</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Left HA Features</TableCell>
                                    </TableRow>
                                    {Object.keys(hearingAidData.features.right).map((feature) => (
                                        <TableRow key={feature}>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    label={feature.charAt(0).toUpperCase() + feature.slice(1)}
                                                    value={hearingAidData.features.right[feature]}
                                                    variant="outlined"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    label={feature.charAt(0).toUpperCase() + feature.slice(1)}
                                                    value={hearingAidData.features.left[feature]}
                                                    variant="outlined"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Ear Mould Section - Two Columns */}
                        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary" sx={{ mt: 2, mb: 2 }}>
                            Ear Mould
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Right Ear Mould</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Left Ear Mould</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Type"
                                                        value={hearingAidData.earMould.right.type}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Material"
                                                        value={hearingAidData.earMould.right.material}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                        <TableCell>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Type"
                                                        value={hearingAidData.earMould.left.type}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Material"
                                                        value={hearingAidData.earMould.left.material}
                                                        variant="outlined"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Questionnaire Section */}
                {demographicData && (
                    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                            Questionnaire
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Questionnaire Type */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel shrink={true}>Questionnaire Type</InputLabel>
                                    <Select
                                        value={questionnaireType}
                                        onChange={(e) => setQuestionnaireType(e.target.value)}
                                        label="Questionnaire Type"
                                        displayEmpty
                                        renderValue={value => value || "Select Questionnaire Type"}
                                    >
                                        <MenuItem value="ABHAB">ABHAB</MenuItem>
                                        <MenuItem value="HHIA">HHIA</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Conditioning Type */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel shrink={true}>Conditioning</InputLabel>
                                    <Select
                                        value={conditioningType}
                                        onChange={(e) => setConditioningType(e.target.value)}
                                        label="Conditioning"
                                        displayEmpty
                                        renderValue={value => value || "Select Conditioning"}
                                    >
                                        <MenuItem value="Unaided">Unaided</MenuItem>
                                        <MenuItem value="Aided">Aided</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* ABHAB Score */}
                            {questionnaireType === 'ABHAB' && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="ABHAP Score"
                                        value={abhapScore}
                                        onChange={(e) => setAbhapScore(e.target.value)}
                                        variant="outlined"
                                        type="number"
                                    />
                                </Grid>
                            )}

                            {/* HHIA Scores */}
                            {questionnaireType === 'HHIA' && (
                                <>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Total Score"
                                            value={hhiaScores.total}
                                            onChange={(e) => handleHhiaChange('total', e.target.value)}
                                            variant="outlined"
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Emotional Score"
                                            value={hhiaScores.emotional}
                                            onChange={(e) => handleHhiaChange('emotional', e.target.value)}
                                            variant="outlined"
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Social Score"
                                            value={hhiaScores.social}
                                            onChange={(e) => handleHhiaChange('social', e.target.value)}
                                            variant="outlined"
                                            type="number"
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                        {/* Password Modal */}
                        <Dialog
                            open={openPasswordModal}
                            onClose={handlePasswordModalClose}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle>
                                Download Data
                                <IconButton
                                    aria-label="close"
                                    onClick={handlePasswordModalClose}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: 8,
                                        color: (theme) => theme.palette.grey[500],
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent>
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        variant="outlined"
                                        sx={{ mb: 3 }}
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Format</InputLabel>
                                        <Select
                                            value={downloadFormat}
                                            onChange={(e) => setDownloadFormat(e.target.value)}
                                            label="Format"
                                        >
                                            <MenuItem value="excel">Excel</MenuItem>
                                            <MenuItem value="pdf">PDF</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 2 }}>
                                <Button onClick={handlePasswordModalClose} color="inherit">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDownloadConfirm}
                                    variant="contained"
                                    color="primary"
                                    disabled={!password}
                                >
                                    Download
                                </Button>
                            </DialogActions>
                        </Dialog>
                        {/* Submit Button */}
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={!questionnaireType || !conditioningType}
                                sx={{ mt: 2, px: 4, py: 1.5, fontSize: '1.1rem' }}
                            >
                                Submit Follow-up
                            </Button>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Paper>
    );
};

export default HearingFollow;